import React, { useState, useEffect } from 'react';
import { getData, saveData, db, getUserProfile } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { invoiceSettlement } from '../ledgers';

const BRAND = '#0f6e56';

// mode = 'receipt' (money from customers) or 'payment' (money to suppliers)
export default function ReceiptPayment({ user, mode }) {
  const isReceipt = mode === 'receipt';
  const collectionName = isReceipt ? 'receipts' : 'payments';
  const invType = isReceipt ? 'sales' : 'purchase';
  const partyField = isReceipt ? 'customerName' : 'supplierName';
  const accent = isReceipt ? BRAND : '#a32d2d';
  const title = isReceipt ? 'Receipts' : 'Payments';
  const subtitle = isReceipt ? 'Record money received from customers and settle their invoices' : 'Record money paid to suppliers and settle their bills';

  const [vouchers, setVouchers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState('USD');

  const load = async () => {
    setLoading(true);
    const [v, inv, p] = await Promise.all([getData(user.uid, collectionName), getData(user.uid, 'invoices'), getData(user.uid, 'parties')]);
    getUserProfile(user.uid).then(pr => { if (pr && pr.currency) setBaseCurrency(pr.currency); });
    setVouchers(v); setInvoices(inv); setParties(p);
    setLoading(false);
  };

  useEffect(() => { load(); }, [mode]);

  const del = async (id) => {
    if (!window.confirm('Delete this voucher? Linked invoices will reopen.')) return;
    await deleteDoc(doc(db, 'users', user.uid, collectionName, id));
    load();
  };

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totalReceived = vouchers.reduce((s, v) => s + (Number(v.amount) || 0), 0);
  const sorted = [...vouchers].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>{title}</h2>
        <button onClick={() => setShowForm(true)} style={{ padding: '10px 20px', background: accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>+ New {isReceipt ? 'Receipt' : 'Payment'}</button>
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>{subtitle}</div>

      <div style={{ background: isReceipt ? '#e1f5ee' : '#fcebeb', borderRadius: 14, padding: 20, marginBottom: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: accent, marginBottom: 4 }}>Total {isReceipt ? 'received' : 'paid'}</div>
        <div style={{ fontSize: 32, fontWeight: 700, color: accent }}>{fmt(totalReceived)}</div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{vouchers.length} voucher{vouchers.length !== 1 ? 's' : ''}</div>
      </div>

      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{isReceipt ? '💰' : '💸'}</div>
          <div style={{ fontSize: 16, fontWeight: 500, color: '#aaa' }}>No {title.toLowerCase()} yet</div>
          <div style={{ fontSize: 13, color: '#ccc', marginTop: 4 }}>Click "+ New {isReceipt ? 'Receipt' : 'Payment'}" to record one</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sorted.map(v => (
            <div key={v.id} style={{ background: '#fff', borderRadius: 12, padding: 18, border: '1px solid #e8e8e8' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: isReceipt ? '#e1f5ee' : '#fcebeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{isReceipt ? '💰' : '💸'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{v.partyName}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{v.voucherNo} · {v.date} · {v.method === 'bank' ? '🏦 Bank' : '💵 Cash'}{v.reference ? ` · Ref: ${v.reference}` : ''}</div>
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: accent }}>{fmt(v.amount)}</div>
                <button onClick={() => del(v.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e24b4a', fontSize: 16 }}>🗑</button>
              </div>
              {v.allocations && v.allocations.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 6, textTransform: 'uppercase' }}>Settled against</div>
                  {v.allocations.map((a, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                      <span style={{ color: '#666' }}>{a.invoiceNo}</span>
                      <span style={{ fontWeight: 500 }}>{fmt(a.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <VoucherForm
          user={user} mode={mode} invoices={invoices} parties={parties} vouchers={vouchers}
          existingCount={vouchers.length}
          baseCurrency={baseCurrency}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function VoucherForm({ user, mode, invoices, parties, vouchers, existingCount, baseCurrency = 'USD', onClose, onSaved }) {
  const isReceipt = mode === 'receipt';
  const collectionName = isReceipt ? 'receipts' : 'payments';
  const invType = isReceipt ? 'sales' : 'purchase';
  const partyField = isReceipt ? 'customerName' : 'supplierName';
  const partyType = isReceipt ? 'customer' : 'supplier';
  const accent = isReceipt ? BRAND : '#a32d2d';
  const allReceipts = isReceipt ? vouchers : [];
  const allPayments = isReceipt ? [] : vouchers;

  const [partyName, setPartyName] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [allocMode, setAllocMode] = useState('auto'); // auto | manual
  const [manualAlloc, setManualAlloc] = useState({}); // invoiceId -> amount
  const [settlementRate, setSettlementRate] = useState(''); // rate on settlement day for foreign invoices

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Open invoices for the selected party
  const openInvoices = invoices
    .filter(i => i.type === invType && i[partyField] === partyName)
    .map(i => ({ ...i, settle: invoiceSettlement(i, allReceipts, allPayments) }))
    .filter(i => i.settle.outstanding > 0.01)
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  const partyList = parties.filter(p => p.type === partyType);
  const totalOpen = openInvoices.reduce((s, i) => s + i.settle.outstanding, 0);
  // Detect a single foreign currency among the open invoices (enables forex settlement)
  const foreignOpen = openInvoices.filter(i => i.currency && i.currency !== baseCurrency);
  const foreignCur = foreignOpen.length && foreignOpen.every(i => i.currency === foreignOpen[0].currency) ? foreignOpen[0].currency : null;
  const setRate = parseFloat(settlementRate) || 0;

  // Build allocations based on mode
  const buildAllocations = () => {
    const amt = parseFloat(amount) || 0;
    if (allocMode === 'manual') {
      return openInvoices
        .filter(i => (parseFloat(manualAlloc[i.id]) || 0) > 0)
        .map(i => ({ invoiceId: i.id, invoiceNo: i.invoiceNo, amount: Math.min(parseFloat(manualAlloc[i.id]) || 0, i.settle.outstanding) }));
    }
    // auto — oldest first
    let remaining = amt;
    const allocs = [];
    for (const inv of openInvoices) {
      if (remaining <= 0.01) break;
      const apply = Math.min(remaining, inv.settle.outstanding);
      allocs.push({ invoiceId: inv.id, invoiceNo: inv.invoiceNo, amount: apply });
      remaining -= apply;
    }
    return allocs;
  };

  const allocations = buildAllocations();
  const allocatedTotal = allocations.reduce((s, a) => s + a.amount, 0); // base at each invoice's booking rate
  // Cash actually moved: for foreign allocations, base = allocBase * (settlementRate / invoiceRate)
  const cashFromAllocs = allocations.reduce((sum, a) => {
    const inv = invoices.find(x => x.id === a.invoiceId);
    if (inv && inv.currency && inv.currency !== baseCurrency && setRate > 0) {
      const invRate = Number(inv.fxRate) || 1;
      return sum + a.amount * (setRate / invRate);
    }
    return sum + a.amount;
  }, 0);
  const forexAmount = cashFromAllocs - allocatedTotal; // +ve gain for receipts
  const manualTotal = Object.values(manualAlloc).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const effectiveAmount = allocMode === 'manual' ? manualTotal : (parseFloat(amount) || 0);

  const save = async (e) => {
    e.preventDefault();
    if (!partyName) { alert('Please select a party'); return; }
    const baseFinal = allocMode === 'manual' ? manualTotal : (parseFloat(amount) || 0);
    if (baseFinal <= 0) { alert('Enter an amount or allocation'); return; }
    if (foreignCur && setRate <= 0) { alert('Please enter the settlement exchange rate for ' + foreignCur); return; }
    const prefix = isReceipt ? 'RCP' : 'PMT';
    const voucherNo = `${prefix}-${String(existingCount + 1).padStart(4, '0')}`;
    // When settling foreign invoices, cash amount reflects the settlement-day rate; relief clears the booked base
    const useForex = !!foreignCur && setRate > 0 && Math.abs(forexAmount) > 0.005;
    const cashAmount = useForex ? cashFromAllocs : baseFinal;
    const reliefField = isReceipt ? 'arRelief' : 'apRelief';
    const record = {
      voucherNo, partyName, amount: cashAmount, method, date, reference,
      allocations, unallocated: Math.max(0, baseFinal - allocatedTotal),
    };
    if (useForex) { record[reliefField] = allocatedTotal; record.settlementRate = setRate; record.forexCurrency = foreignCur; }
    await saveData(user.uid, collectionName, record);
    onSaved();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '92vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>New {isReceipt ? 'Receipt' : 'Payment'} Voucher</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>{isReceipt ? 'Money received from a customer' : 'Money paid to a supplier'} — settle against open {isReceipt ? 'invoices' : 'bills'}</div>

        <form onSubmit={save}>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>{isReceipt ? 'Customer' : 'Supplier'} *</label>
            <select value={partyName} onChange={e => { setPartyName(e.target.value); setManualAlloc({}); }} style={inp}>
              <option value="">Select {partyType}...</option>
              {partyList.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>

          {partyName && (
            <div style={{ background: '#f7f8fa', borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#666' }}>Total outstanding</span>
                <span style={{ fontWeight: 700, color: accent }}>{fmt(totalOpen)}</span>
              </div>
              <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{openInvoices.length} open {isReceipt ? 'invoice' : 'bill'}{openInvoices.length !== 1 ? 's' : ''}</div>
            </div>
          )}

          {partyName && foreignCur && (
            <div style={{ background: '#f5f8fc', border: '1px solid #dce7f3', borderRadius: 10, padding: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#185fa5', marginBottom: 8 }}>💱 Foreign settlement ({foreignCur})</div>
              <label style={lbl}>Settlement rate today (1 {foreignCur} = ? {baseCurrency}) *</label>
              <input type="number" step="any" value={settlementRate} onChange={e => setSettlementRate(e.target.value)} placeholder="e.g. 3.64" style={inp} />
              {setRate > 0 && Math.abs(forexAmount) > 0.005 && (
                <div style={{ fontSize: 12, marginTop: 8, color: forexAmount >= 0 ? '#0f6e56' : '#a32d2d' }}>
                  {forexAmount >= 0 ? 'Forex gain' : 'Forex loss'}: {baseCurrency} {Math.abs(forexAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span style={{ color: '#999' }}> — cash {baseCurrency} {cashFromAllocs.toFixed(2)} vs booked {allocatedTotal.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Method</label>
              <select value={method} onChange={e => setMethod(e.target.value)} style={inp}>
                <option value="cash">💵 Cash</option>
                <option value="bank">🏦 Bank</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Reference (cheque/txn no)</label>
            <input value={reference} onChange={e => setReference(e.target.value)} placeholder="Optional" style={inp} />
          </div>

          {partyName && openInvoices.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button type="button" onClick={() => setAllocMode('auto')} style={{ flex: 1, padding: 9, borderRadius: 8, border: `1.5px solid ${allocMode === 'auto' ? accent : '#ddd'}`, background: allocMode === 'auto' ? accent : '#fff', color: allocMode === 'auto' ? '#fff' : '#555', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Auto (oldest first)</button>
                <button type="button" onClick={() => setAllocMode('manual')} style={{ flex: 1, padding: 9, borderRadius: 8, border: `1.5px solid ${allocMode === 'manual' ? accent : '#ddd'}`, background: allocMode === 'manual' ? accent : '#fff', color: allocMode === 'manual' ? '#fff' : '#555', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Pick invoices</button>
              </div>

              {allocMode === 'auto' && (
                <div style={{ marginBottom: 14 }}>
                  <label style={lbl}>Amount received *</label>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" style={inp} autoFocus />
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button type="button" onClick={() => setAmount(String(totalOpen))} style={chip}>Full ({fmt(totalOpen)})</button>
                    {openInvoices[0] && <button type="button" onClick={() => setAmount(String(openInvoices[0].settle.outstanding))} style={chip}>Oldest bill</button>}
                  </div>
                </div>
              )}

              <div style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                <div style={{ background: '#f7f8fa', padding: '8px 14px', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Open {isReceipt ? 'invoices' : 'bills'} — bill-to-bill</div>
                {openInvoices.map(inv => {
                  const autoAlloc = allocations.find(a => a.invoiceId === inv.id);
                  return (
                    <div key={inv.id} style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{inv.invoiceNo}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{inv.date} · Outstanding {fmt(inv.settle.outstanding)}{inv.settle.status === 'partial' ? ' (partial)' : ''}</div>
                      </div>
                      {allocMode === 'manual' ? (
                        <input type="number" value={manualAlloc[inv.id] || ''} onChange={e => setManualAlloc(m => ({ ...m, [inv.id]: e.target.value }))} placeholder="0.00"
                          style={{ width: 100, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, textAlign: 'right' }} />
                      ) : (
                        <div style={{ fontSize: 13, fontWeight: 600, color: autoAlloc ? accent : '#ccc', minWidth: 90, textAlign: 'right' }}>{autoAlloc ? fmt(autoAlloc.amount) : '—'}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ background: '#e1f5ee', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#085041' }}>{isReceipt ? 'Receipt' : 'Payment'} total</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: BRAND }}>{fmt(effectiveAmount)}</span>
              </div>
            </>
          )}

          {partyName && openInvoices.length === 0 && (
            <div style={{ textAlign: 'center', padding: 20, color: '#aaa', fontSize: 14, background: '#f7f8fa', borderRadius: 10, marginBottom: 16 }}>
              ✅ No open {isReceipt ? 'invoices' : 'bills'} for this {partyType}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" style={{ flex: 2, padding: 12, background: accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>Save {isReceipt ? 'Receipt' : 'Payment'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const lbl = { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 };
const inp = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 10, fontSize: 14, color: '#222', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const chip = { padding: '5px 12px', borderRadius: 16, border: '1px solid #cce4d9', background: '#e1f5ee', color: '#0f6e56', cursor: 'pointer', fontSize: 12, fontWeight: 600 };
